/**
 * Stage catalog — colors, icons and groups vendored from the original skin's
 * product-specific stage catalog (`.node-design/original-skin/core/runtime/stages.js`'s
 * `icons`/`groups`/`items` — the source `#pmMore` "Stages + more" mega-panel is built
 * straight from this registry, see `core/shell/menu/index.js`'s own
 * `renderMoreMenuFromRegistry()`).
 *
 * This app's stage catalog is dynamic (`stages.installed.v1`, `runtime/stage-registry.ts`,
 * `runtime/stage-router.ts`'s `appNameFromPath`) rather than the original's fixed product
 * list, so this module supplies:
 *  - the same 5 colored groups (id/label/color), in the same order, the original panel
 *    sections its columns by;
 *  - the same icon-name -> SVG-path vocabulary, so any KNOWN stage/app id renders the exact
 *    original tile artwork (not a re-drawn approximation);
 *  - a static id -> {group, icon} map covering every id the original `items` array
 *    declares (kept verbatim, even for ids this app's real catalog doesn't use yet — a
 *    future stage installed under one of the original's own ids picks up its original
 *    placement for free), PLUS this product's own real installed-app ids (this repo's
 *    `modules/*\/manifest.json` "name" field: home/agents/friends/chat/feeds/peers/
 *    graphs-ui/ldk-node/operations/notifications-ui/settings-ui) mapped onto the
 *    closest-matching original group/icon so today's real catalog also renders sensibly,
 *    not only the original's product list.
 *
 * Any appName NOT covered by either source (the common case for a live, ever-growing
 * registry) resolves to the STAGES group with a generic fallback tile
 * (`resolveStageCatalogEntry`) rather than being dropped or guessed into the wrong group.
 */
/** Verbatim from the source's `groups` array — order AND colors both matter (the order is
 * the flyout's column order; the colors are the `--sec` custom property every `.pmore-sec`/
 * `.pmore-chip`/hover-tint rule in the vendored `menu.css` reads). */
export const STAGE_GROUPS = [
    { color: "#DF6662", id: "default", label: "Default" },
    { color: "#36AA75", id: "agents", label: "Agents" },
    { color: "#98A6CF", id: "stages", label: "Stages" },
    { color: "#2D7E5F", id: "network", label: "Network" },
    { color: "#C0CAE8", id: "system", label: "System" },
];
/** Verbatim from the source's `icons` map — inner `<path>`/`<circle>`/`<rect>`/... markup
 * for a `viewBox="0 0 20 20"` SVG. The caller wraps this in its own `<svg ...>` (matching
 * `core/shell/menu/index.js`'s own `svg(name)` helper, which does the same). */
const STAGE_ICONS = {
    agents: '<path d="M10 2 L11.8 7.2 L17 9 L11.8 10.8 L10 16 L8.2 10.8 L3 9 L8.2 7.2 Z"/>',
    browser: '<path d="M4 5 H16 V15 H4 Z"/><path d="M4 8 H16"/><circle cx="6" cy="6.5" r=".4"/>',
    builder: '<circle cx="5" cy="5" r="2"/><circle cx="15" cy="5" r="2"/><circle cx="10" cy="15" r="2"/><path d="M6.3 6.6 L9 13 M13.7 6.6 L11 13"/>',
    characters: '<circle cx="7" cy="7" r="2.5"/><circle cx="13.5" cy="8" r="2"/><path d="M3 16 Q3 12 7 12 Q11 12 11 16 M10.5 16 Q11 13.5 13.5 13.5 Q17 13.5 17 16"/>',
    component: '<path d="M5 4 H15 V16 H5 Z"/><path d="M8 7 H12 M8 10 H12 M8 13 H10"/>',
    database: '<ellipse cx="10" cy="5" rx="6.5" ry="2.4"/><path d="M3.5 5 V10 Q3.5 12.4 10 12.4 Q16.5 12.4 16.5 10 V5 M3.5 10 V15 Q3.5 17.4 10 17.4 Q16.5 17.4 16.5 15 V10"/>',
    device: '<rect x="6" y="3" width="8" height="14" rx="3"/><path d="M9 14 H11"/>',
    friends: '<circle cx="7" cy="8" r="3"/><circle cx="14" cy="9" r="2.5"/><path d="M2.5 17 Q2.5 12 7 12 Q11.5 12 11.5 17 M11 17 Q11 13.5 14 13.5 Q17.5 13.5 17.5 17"/>',
    help: '<circle cx="10" cy="10" r="7"/><path d="M8.2 8 Q8.4 6.5 10 6.5 Q11.7 6.5 11.7 8 Q11.7 9 10.6 9.8 Q10 10.2 10 11.2"/><path d="M10 14 H10.01"/>',
    home: '<path d="M3 10 L10 4 L17 10 M5 9 V16 H15 V9"/>',
    memory: '<rect x="3" y="3" width="6" height="6" rx="1"/><rect x="11" y="3" width="6" height="6" rx="1"/><rect x="3" y="11" width="6" height="6" rx="1"/><rect x="11" y="11" width="6" height="6" rx="1" fill="currentColor" stroke="none"/>',
    network: '<circle cx="10" cy="10" r="2.2"/><circle cx="3.5" cy="4" r="1.7"/><circle cx="16.5" cy="4" r="1.7"/><circle cx="3.5" cy="16" r="1.7"/><circle cx="16.5" cy="16" r="1.7"/><path d="M5 5.3 L8.2 8.4 M15 5.3 L11.8 8.4 M5 14.7 L8.2 11.6 M15 14.7 L11.8 11.6"/>',
    orbit: '<circle cx="10" cy="10" r="4.5"/><ellipse cx="10" cy="10" rx="8.5" ry="3.2" transform="rotate(-18 10 10)"/>',
    profile: '<circle cx="10" cy="7" r="3.4"/><path d="M3.5 17.5 Q3.5 11.5 10 11.5 Q16.5 11.5 16.5 17.5"/>',
    robot: '<path d="M10 5.5 V3 M7.8 3 H12.2"/><rect x="5" y="5.5" width="10" height="7.5"/><rect x="7.2" y="8" width="1.6" height="1.8" fill="currentColor" stroke="none"/><rect x="11.2" y="8" width="1.6" height="1.8" fill="currentColor" stroke="none"/><path d="M6.5 16.5 H13.5"/>',
    store: '<rect x="3" y="3" width="6" height="6" rx="1.4"/><rect x="11" y="3" width="6" height="6" rx="1.4"/><rect x="3" y="11" width="6" height="6" rx="1.4"/><path d="M14 11.5 V16.5 M11.5 14 H16.5"/>',
    tools: '<path d="M4 14 L10 8"/><path d="M7 5 L9 3 L12 6 L10 8"/><path d="M12 12 L16 16"/>',
    trophy: '<path d="M4 5 H16 V8 Q16 11 13 12.5 L10 14 L7 12.5 Q4 11 4 8 Z"/><path d="M8 17 H12 M10 14 V17"/>',
    wallet: '<path d="M3 6 Q3 4 5 4 H15 Q17 4 17 6 V15 Q17 17 15 17 H5 Q3 17 3 15 Z"/><path d="M3 8 H17"/><circle cx="13" cy="12.5" r="1" fill="currentColor"/>',
    waves: '<path d="M2.5 7 Q5.5 4 8.5 7 T14.5 7 T20.5 7"/><path d="M2.5 12 Q5.5 9 8.5 12 T14.5 12 T20.5 12"/><path d="M2.5 17 Q5.5 14 8.5 17 T14.5 17 T20.5 17"/>',
    workspace: '<rect x="3" y="3" width="14" height="14" rx="2"/><path d="M7 8 H13 M7 12 H11"/>',
};
/** Fallback tile for any installed app id neither catalog source covers — a plain rounded
 * square, deliberately distinct from every named icon above so it reads as "generic" rather
 * than being mistaken for a real one. */
const GENERIC_ICON = '<rect x="3.5" y="3.5" width="13" height="13" rx="2.5"/>';
/**
 * Static id -> {group, icon} map. Two sources, both documented inline:
 *  1. Every id the original's `core/runtime/stages.js` `items` array declares, with its own
 *     `group`/`icon` fields copied verbatim.
 *  2. This product's OWN real installed-app ids (`modules/*\/manifest.json`'s "name" field),
 *     mapped onto the closest-matching original group/icon — no new icon artwork invented.
 */
const KNOWN_STAGE_CATALOG = {
    // -- 1. original skin vocabulary (verbatim id -> group/icon from `stages.js`'s `items`) --
    "agent-army": { group: "agents", icon: "robot" },
    "agent-characters": { group: "agents", icon: "characters" },
    "agent-editor": { group: "agents", icon: "tools" },
    agents: { group: "agents", icon: "agents" },
    appstore: { group: "default", icon: "store" },
    "browser-memory": { group: "system", icon: "browser" },
    builder: { group: "agents", icon: "builder" },
    "component-sizes": { group: "system", icon: "component" },
    contests: { group: "network", icon: "trophy" },
    dashboard: { group: "default", icon: "home" },
    databases: { group: "default", icon: "database" },
    "developer-toolbar": { group: "system", icon: "tools" },
    device: { group: "network", icon: "device" },
    friends: { group: "default", icon: "friends" },
    help: { group: "system", icon: "help" },
    memory: { group: "default", icon: "memory" },
    network: { group: "network", icon: "network" },
    orbit: { group: "stages", icon: "orbit" },
    profile: { group: "system", icon: "profile" },
    // the original has no "settings" id of its own — this entry is this task's own
    // directive: "Settings -> SYSTEM group".
    settings: { group: "system", icon: "tools" },
    wallet: { group: "network", icon: "wallet" },
    waves: { group: "stages", icon: "waves" },
    workspace: { group: "stages", icon: "workspace" },
    // -- 2. this product's real installed-app ids (`modules/*\/manifest.json` "name") --
    chat: { group: "default", icon: "friends" },
    feeds: { group: "stages", icon: "waves" },
    "graphs-ui": { group: "stages", icon: "workspace" },
    home: { group: "default", icon: "home" },
    "ldk-node": { group: "network", icon: "wallet" },
    "notifications-ui": { group: "system", icon: "help" },
    operations: { group: "system", icon: "tools" },
    peers: { group: "network", icon: "network" },
    "settings-ui": { group: "system", icon: "tools" },
};
/**
 * Resolves an installed app id to its group + icon id. Unknown ids (any stage this catalog
 * has no entry for — the common case for a live registry) fall back to the STAGES group with
 * the generic tile, never dropped and never guessed into a group that doesn't fit.
 */
export function resolveStageCatalogEntry(appName) {
    const known = appName === undefined ? undefined : KNOWN_STAGE_CATALOG[appName];
    return known ?? { group: "stages", icon: undefined };
}
/** Inner SVG markup (`<path>`/`<circle>`/...) for the given icon id — falls back to the
 * generic tile for `undefined`/unrecognized ids. */
export function stageIconMarkup(icon) {
    return (icon !== undefined ? STAGE_ICONS[icon] : undefined) ?? GENERIC_ICON;
}
//# sourceMappingURL=stage-catalog.js.map